import { useMemo } from 'react';

import { Box, ButtonBase, Divider } from '@mui/material';
import { DateRangeIcon } from '@mui/x-date-pickers';
import { format, isValid } from 'date-fns';
import { useNavigate } from 'react-router';

import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { MIN_DATE } from '@/features/DataHub/modules/Export/constants/dateSettings';
import {
  useExportPreview,
  useExportSettings
} from '@/features/DataHub/modules/Export/context/exportContexts';
import { DateRangeType, ExportTypes } from '@/features/DataHub/modules/Export/types';
import { getPeriodLabel } from '@/features/DataHub/utils/getPeriodLabel';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';

import DateRangeSettingsFields from './DateRangeSettingsFields';
import NoDataDateAlert from './NoDataDateAlert';

type ExportSettings = ReturnType<typeof useExportSettings>['exportSettings'];

function getCollapsedSummary(
  exportSettings: ExportSettings,
  timezoneLabel: string,
  dateFormatValue: string
): string {
  const tz = `Timezone: ${timezoneLabel}`;
  switch (exportSettings.dateRangeType) {
    case DateRangeType.ALL:
      return `Export range: All, ${tz}`;
    case DateRangeType.RANGE:
      return `Export range: Last ${exportSettings.periodNumber} ${getPeriodLabel(exportSettings.period, exportSettings.periodNumber)}, ${tz}`;
    case DateRangeType.DATES: {
      const from =
        exportSettings.from && isValid(exportSettings.from)
          ? format(exportSettings.from, dateFormatValue)
          : '';
      const to =
        exportSettings.to && isValid(exportSettings.to)
          ? format(exportSettings.to, dateFormatValue)
          : '';
      return `Export range: ${from} - ${to}, ${tz}`;
    }
    default:
      return '';
  }
}

const DateSettings = ({
  defaultExpanded = false,
  showNoDataAlert,
  periodOptions
}: {
  defaultExpanded?: boolean;
  showNoDataAlert?: boolean;
  periodOptions?: Array<{ value: string; label: string }>;
}) => {
  const navigate = useNavigate();
  const { exportSettings } = useExportSettings();
  const { previewHasNoData } = useExportPreview();

  const exportType = useAppSelector(state => state.export.type);

  const { timezoneLabel, dateFormatValue } = useDataHubUserConfig();

  const dispatch = useAppDispatch();
  const shouldShowNoDataAlert =
    showNoDataAlert ?? (exportSettings.exportType === ExportTypes.IMAGE && previewHasNoData);

  const collapsedSummary = useMemo(
    () => getCollapsedSummary(exportSettings, timezoneLabel, dateFormatValue),
    [exportSettings, timezoneLabel, dateFormatValue]
  );

  if (
    exportSettings.exportType === ExportTypes.IMAGE &&
    (exportType === 'seasonal' || exportType === 'forwardSimple')
  ) {
    return null;
  }
  return (
    <DataHubCard
      expandable
      collapsedSummary={collapsedSummary}
      defaultExpanded={defaultExpanded}
      icon={
        <DateRangeIcon
          sx={{
            fontSize: '20px'
          }}
        />
      }
      subtitle='Select date range and timezone options'
      title='Export settings'
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontSize: '14px'
        }}
      >
        {shouldShowNoDataAlert && <NoDataDateAlert />}
        <DateRangeSettingsFields
          dateFormat={dateFormatValue}
          maxDate={new Date()}
          minDate={MIN_DATE}
          periodOptions={periodOptions}
        />
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 2
          }}
        >
          <div>
            <span style={{ fontWeight: 'bold' }}>Timezone:</span> {timezoneLabel}
          </div>
          <ButtonBase
            sx={{
              color: 'primary.main'
            }}
            onClick={() => {
              navigate('/profile', { replace: true });
              dispatch(closeDrawer());
            }}
          >
            Change in Settings
          </ButtonBase>
        </Box>
      </Box>
    </DataHubCard>
  );
};

export default DateSettings;
